/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-continue */
/* eslint-disable no-plusplus */
import { getConfigStorageItem } from '../hooks/useConfigStorage';
import type { StructureType } from '../types/types';

export const isJson = (item: string) => {
	let value = typeof item !== 'string' ? JSON.stringify(item) : item;
	try {
		value = JSON.parse(value);
	} catch (e) {
		return false;
	}
	return typeof value === 'object' && value !== null;
};

export const resolveObjPathDots = (obj: object, path: string) => {
	// Callers pass grid values of any shape; `path in obj` throws on primitives.
	if (!obj || typeof obj !== 'object' || !path) return undefined;
	const record = obj as Record<string, unknown>;
	if (path in record) {
		return record[path];
	}
	return path.split('.').reduce<unknown>((acc, key) => {
		if (acc === null || acc === undefined) return undefined;
		return (acc as Record<string, unknown>)[key];
	}, record);
};

export const findStructure = (
	structure: StructureType[],
	key: string,
): StructureType => {
	const isModelChild = key.includes('.');
	const keys = isModelChild ? key.split('.') : [key];
	const stack: { level: StructureType[]; index: number }[] = [
		{ level: structure, index: 0 },
	];
	let currentKeyIndex = 0;
	while (stack.length > 0) {
		const current = stack[stack.length - 1];
		if (current.index >= current.level.length) {
			stack.pop();
			currentKeyIndex--;
			continue;
		}
		const item = current.level[current.index];
		current.index++;
		if (item.Name === keys[currentKeyIndex]) {
			if (currentKeyIndex === keys.length - 1) {
				return item;
			}
			if (item.InnerStructure && item.InnerStructure.length > 0) {
				stack.push({ level: item.InnerStructure, index: 0 });
				currentKeyIndex++;
			}
		}
	}
	return null as any;
};

export const getInnerStructureNames = (
	structure: StructureType[],
	key: string,
	currentPath?: string,
): string[] => {
	const result = [] as Array<string>;
	structure.forEach((item) => {
		const currentKey = currentPath ? `${currentPath}.${item.Name}` : item.Name;
		if (key && currentKey === key) {
			if (item.InnerStructure && Array.isArray(item.InnerStructure)) {
				result.push(...getInnerStructureNames(item.InnerStructure, '', key));
			}
			return;
		}
		if (!key) {
			result.push(currentKey);
		}
		if (item.InnerStructure && Array.isArray(item.InnerStructure)) {
			result.push(
				...getInnerStructureNames(item.InnerStructure, key, currentKey),
			);
		}
	});
	return result;
};

const getTranslationFromStorage = (key: string): string | null => {
	try {
		const translations = getConfigStorageItem('translations') || {};
		const tableNames = Object.keys(translations);
		for (let i = 0; i < tableNames.length; i++) {
			const tableName = tableNames[i];
			if (
				translations[tableName] &&
				typeof translations[tableName] === 'object' &&
				translations[tableName][key]
			) {
				return translations[tableName][key];
			}
		}
	} catch {
		try {
			const lsTranslations = localStorage.getItem('translations');
			if (lsTranslations) {
				const translations = JSON.parse(lsTranslations);
				if (translations[key]) {
					return translations[key];
				}
			}
		} catch {
			return null;
		}
	}
	return null;
};

export const getTranslation = (
	structure: StructureType[],
	key: string,
	onlyInnerTranslation?: boolean,
): string => {
	const storedTranslation = getTranslationFromStorage(key);
	if (storedTranslation) {
		return storedTranslation;
	}
	const nameParts = key.split('.');
	const findTranslation = (
		parts: Array<string>,
		innerStructure: StructureType[],
	): string => {
		const [current, ...rest] = parts;
		for (const item of innerStructure) {
			if (item.Name === current) {
				if (rest.length === 0) {
					return item.Translation;
				}
				if (item.InnerStructure) {
					const innerTranslation = findTranslation(rest, item.InnerStructure);
					if (innerTranslation) {
						return onlyInnerTranslation
							? innerTranslation
							: `${item.Translation}.${innerTranslation}`;
					}
				}
			}
		}
		return 'no translation';
	};
	return findTranslation(nameParts, structure);
};
